/**
 * AAC Prediction Engine (Complete Implementation)
 * Implements N-Gram Backoff (Trigram -> Bigram -> Unigram),
 * prefix search, phrase-pair detection,
 * and Hybrid Cloud/Local prediction.
 */

import {
  detectLanguage as detectLang,
  searchVocabulary,
  getVocabulary,
  type SupportedLanguage,
} from "./vocabulary";
import type { UserWordLearning, LearnedWord, LearnedBigram } from "./aacLearningSync";
import { seedDictionary } from "./aacDictionary";
import { supabase } from "@/integrations/supabase/client";

// ==================== Types ====================

export interface WordEntry {
  word: string;
  frequency: number;
  lastUsed: number;
  language: SupportedLanguage;
  isNextWord?: boolean;
  isPhrasePair?: boolean;
  source?: "local" | "ai";
}

export interface PhraseEntry {
  phrase: string;
  acronym: string;
  frequency: number;
  lastUsed: number;
  language: SupportedLanguage;
}

// ==================== Constants ====================

const STORAGE_KEY_WORDS = "aac_learned_words_v3";
const STORAGE_KEY_BIGRAMS = "aac_bigrams_v3";
const STORAGE_KEY_PHRASES = "aac_phrases_v3";
const STORAGE_KEY_RECENT = "aac_recent_words_v3";
const STORAGE_KEY_PHRASE_PAIRS = "aac_phrase_pairs_v3";
const RECENCY_BOOST_WINDOW = 5 * 60 * 1000;
const MAX_SUGGESTIONS = 6;
const PHRASE_PAIR_THRESHOLD = 30;

// ==================== AAC-Priority Vocabulary ====================
// Words ranked by relevance to AAC users (medical, care, emotions, daily needs).
// These are checked BEFORE the general 50k vocabulary in the unigram fallback.

const AAC_PRIORITY_WORDS: Record<string, string[]> = {
  he: [
    "כואב", "כאב", "צמא", "רעב", "עייף", "קר", "חם", "שירותים", "מים", "תרופות",
    "עזרה", "רופא", "אחות", "מטפל", "שמיכה", "כרית", "מיטה", "כיסא", "משקפיים",
    "אוכל", "שתייה", "מקלחת", "בבקשה", "תודה", "כן", "לא", "אולי", "עוד", "מספיק",
    "שמח", "עצוב", "פוחד", "דואג", "מבולבל", "מתוסכל", "רגוע", "בסדר", "טוב",
    "משפחה", "אבא", "אמא", "בן", "בת", "נכד", "נכדה", "אוהב", "אוהבת",
    "טלוויזיה", "מוסיקה", "רדיו", "אור", "חושך", "שקט", "חלון", "דלת",
    "סאקשן", "נשימה", "תנוחה", "לחץ", "סחרחורת", "בחילה", "חולשה", "גירוד",
  ],
  en: [
    "pain", "hurts", "thirsty", "hungry", "tired", "cold", "hot", "bathroom", "water", "medicine",
    "help", "doctor", "nurse", "caregiver", "blanket", "pillow", "bed", "chair", "glasses",
    "food", "drink", "shower", "please", "thanks", "yes", "no", "maybe", "more", "enough",
    "happy", "sad", "scared", "worried", "confused", "frustrated", "calm", "okay", "fine",
    "family", "father", "mother", "son", "daughter", "love", "miss",
    "television", "music", "radio", "light", "dark", "quiet", "window", "door",
    "suction", "breathing", "position", "pressure", "dizzy", "nausea", "weak", "itchy",
    "uncomfortable", "comfortable", "asleep", "awake", "rest",
  ],
  ar: [
    "ألم", "عطشان", "جائع", "متعب", "بارد", "حار", "حمام", "ماء", "دواء",
    "مساعدة", "طبيب", "ممرضة", "بطانية", "وسادة", "سرير", "كرسي",
    "طعام", "شراب", "من فضلك", "شكرا", "نعم", "لا", "ربما", "المزيد", "كافي",
    "سعيد", "حزين", "خائف", "قلق", "مرتبك", "هادئ", "بخير",
    "عائلة", "أب", "أم", "ابن", "ابنة", "أحب",
  ],
  ru: [
    "боль", "больно", "пить", "голод", "устал", "холодно", "жарко", "туалет", "вода", "лекарства",
    "помощь", "врач", "медсестра", "одеяло", "подушка", "кровать", "кресло", "очки",
    "еда", "пожалуйста", "спасибо", "да", "нет", "может", "ещё", "достаточно",
    "счастлив", "грустно", "страшно", "беспокойно", "спокойно", "хорошо",
    "семья", "отец", "мать", "сын", "дочь", "люблю",
  ],
};

// ==================== Helpers ====================

function detectLanguage(text: string): SupportedLanguage {
  return detectLang(text);
}

// ==================== Core Class ====================

class AACPredictionEngine {
  private learnedWords: Map<string, WordEntry> = new Map();
  private bigrams: Record<string, Record<string, number>> = {};
  private phrases: Map<string, PhraseEntry> = new Map();
  private recentWords: { word: string; timestamp: number }[] = [];
  private phrasePairs: Record<string, Record<string, number>> = {};
  private onChangeCallback: (() => void) | null = null;

  constructor() {
    this.loadFromStorage();
    this.seedCommonBigrams();
    this.seedFromDictionary();
  }

  // ==================== Storage ====================

  private loadFromStorage(): void {
    try {
      const wordsJson = localStorage.getItem(STORAGE_KEY_WORDS);
      if (wordsJson) {
        const arr: WordEntry[] = JSON.parse(wordsJson);
        arr.forEach((w) => this.learnedWords.set(w.word, w));
      }

      const bigramsJson = localStorage.getItem(STORAGE_KEY_BIGRAMS);
      if (bigramsJson) {
        this.bigrams = JSON.parse(bigramsJson);
      }

      const phrasesJson = localStorage.getItem(STORAGE_KEY_PHRASES);
      if (phrasesJson) {
        const arr: PhraseEntry[] = JSON.parse(phrasesJson);
        arr.forEach((p) => this.phrases.set(p.acronym + ":" + p.phrase, p));
      }

      const recentJson = localStorage.getItem(STORAGE_KEY_RECENT);
      if (recentJson) {
        this.recentWords = JSON.parse(recentJson);
      }

      const pairsJson = localStorage.getItem(STORAGE_KEY_PHRASE_PAIRS);
      if (pairsJson) {
        this.phrasePairs = JSON.parse(pairsJson);
      }
    } catch (e) {
      console.error("[PredictionEngine] Failed to load from storage:", e);
    }
  }

  /**
   * Seed common word pairs (bigrams) so next-word prediction works
   * immediately for new users without requiring 30+ prior uses.
   * Only adds entries that don't already exist in learned data.
   */
  private seedCommonBigrams(): void {
    const seedBigrams: [string, string, number][] = [
      // ── Hebrew: greetings & basics ──
      ["בוקר", "טוב", 50], ["לילה", "טוב", 50], ["ערב", "טוב", 50],
      ["שבת", "שלום", 50], ["תודה", "רבה", 50],
      ["כן", "בבקשה", 45], ["לא", "תודה", 45],
      // ── Hebrew: pronouns → verbs ──
      ["אני", "רוצה", 50], ["אני", "צריך", 50], ["אני", "מרגיש", 45],
      ["אני", "אוהב", 40], ["אני", "לא", 40], ["אני", "חושב", 40],
      ["אני", "יכול", 40], ["אני", "חייב", 35], ["אני", "מבקש", 35],
      ["אתה", "יכול", 40], ["אתה", "רוצה", 35], ["אתה", "צריך", 35],
      ["הוא", "רוצה", 35], ["הוא", "צריך", 35], ["הוא", "בא", 35],
      ["היא", "רוצה", 35], ["היא", "צריכה", 35], ["היא", "באה", 35],
      // ── Hebrew: question words → natural continuations ──
      ["מה", "קורה", 50], ["מה", "שלומך", 50], ["מה", "נשמע", 50],
      ["מה", "זה", 40], ["מה", "אתה", 35], ["מה", "את", 35],
      ["איך", "אפשר", 45], ["איך", "אתה", 40], ["איך", "זה", 40],
      ["איך", "קוראים", 35], ["איך", "הולך", 35], ["איך", "מרגיש", 35],
      ["איפה", "הוא", 45], ["איפה", "היא", 45], ["איפה", "נמצא", 40],
      ["איפה", "יש", 40], ["איפה", "אני", 35], ["איפה", "זה", 35],
      ["למה", "לא", 45], ["למה", "זה", 40], ["למה", "אתה", 35],
      ["מתי", "הוא", 40], ["מתי", "היא", 40], ["מתי", "נגיע", 35],
      ["מתי", "יבוא", 35], ["מתי", "תבוא", 35],
      ["מי", "זה", 45], ["מי", "בא", 40], ["מי", "יכול", 35],
      ["כמה", "זמן", 40], ["כמה", "זה", 40], ["כמה", "עולה", 35],
      // ── Hebrew: modal / common verbs → infinitives ──
      ["רוצה", "לשתות", 50], ["רוצה", "לאכול", 50], ["רוצה", "לישון", 45],
      ["רוצה", "לצאת", 40], ["רוצה", "ללכת", 40], ["רוצה", "לדבר", 35],
      ["צריך", "לקחת", 45], ["צריך", "ללכת", 45], ["צריך", "לאכול", 40],
      ["צריך", "עזרה", 45], ["צריך", "תרופות", 40], ["צריך", "מים", 40],
      ["חייב", "ללכת", 45], ["חייב", "לצאת", 40], ["חייב", "לראות", 35],
      ["חייב", "לשמוע", 35], ["חייב", "לשירותים", 40], ["חייב", "עכשיו", 35],
      ["אפשר", "לקבל", 45], ["אפשר", "עוד", 40], ["אפשר", "לדעת", 35],
      ["אפשר", "להדליק", 35], ["אפשר", "לצאת", 35], ["אפשר", "לסיים", 35],
      ["יכול", "לעזור", 40], ["יכול", "לבוא", 35], ["יכול", "לדבר", 35],
      // ── Hebrew: common verb → continuation ──
      ["נראה", "לי", 50], ["נראה", "טוב", 40], ["נראה", "ש", 35],
      ["בא", "לי", 50], ["בא", "לך", 40],
      ["יש", "לי", 50], ["יש", "להם", 40], ["יש", "פה", 35],
      ["יש", "הרבה", 35], ["יש", "מעט", 35], ["יש", "משהו", 35],
      ["אין", "לי", 50], ["אין", "בעיה", 40], ["אין", "צורך", 35],
      ["כואב", "לי", 50], ["עזרו", "לי", 45], ["עשו", "לי", 35],
      ["תנו", "לי", 45], ["קחו", "לי", 35],
      ["בבקשה", "תעזרו", 40], ["בבקשה", "תביאו", 35], ["בבקשה", "בואו", 35],
      // ── Hebrew: adjective / state ──
      ["מרגיש", "טוב", 45], ["מרגיש", "רע", 40], ["מרגיש", "כאב", 40],
      ["מרגיש", "עייף", 35], ["מרגיש", "קר", 35], ["מרגיש", "חם", 35],
      ["לא", "רוצה", 40], ["לא", "יכול", 40], ["לא", "צריך", 35],
      ["לא", "טוב", 40], ["לא", "נעים", 35], ["לא", "נוח", 35],
      // ── English: greetings & basics ──
      ["good", "morning", 50], ["good", "night", 50], ["good", "afternoon", 45],
      ["thank", "you", 50], ["yes", "please", 45], ["no", "thanks", 40],
      // ── English: pronouns → verbs ──
      ["i", "want", 50], ["i", "need", 50], ["i", "feel", 45],
      ["i", "love", 40], ["i", "am", 45], ["i", "can't", 40],
      ["i", "don't", 40], ["i", "think", 35],
      ["i'm", "tired", 40], ["i'm", "hungry", 40], ["i'm", "thirsty", 40],
      ["i'm", "cold", 35], ["i'm", "hot", 35], ["i'm", "okay", 35],
      ["i'm", "in", 35], ["i'm", "not", 35],
      // ── English: question words ──
      ["how", "are", 45], ["how", "do", 35], ["how", "much", 35],
      ["what", "is", 45], ["what", "happened", 40], ["what", "time", 35],
      ["where", "is", 45], ["where", "are", 40],
      ["when", "will", 40], ["when", "is", 35], ["when", "are", 35],
      ["can", "you", 45], ["can", "i", 40],
      ["please", "help", 45], ["please", "come", 40], ["please", "wait", 35],
      // ── English: verbs → continuations ──
      ["want", "to", 50], ["need", "to", 50], ["have", "to", 45],
      ["it", "hurts", 40], ["it", "is", 35],
      ["feel", "pain", 40], ["feel", "good", 35], ["feel", "sick", 35],
      // ── Arabic ──
      ["صباح", "الخير", 50], ["مساء", "الخير", 50],
      ["أنا", "أريد", 50], ["أنا", "أحتاج", 45], ["أنا", "أشعر", 40],
      ["شكرا", "جزيلا", 45], ["من", "فضلك", 40],
      ["أين", "هو", 40], ["أين", "هي", 40],
      ["كيف", "حالك", 45], ["كيف", "يمكن", 35],
      ["هل", "يمكن", 40], ["هل", "أنت", 35],
      ["أريد", "أن", 45], ["أريد", "ماء", 40],
      ["أحتاج", "إلى", 45], ["أحتاج", "مساعدة", 40],
      // ── Russian ──
      ["доброе", "утро", 50], ["добрый", "день", 45], ["спокойной", "ночи", 45],
      ["я", "хочу", 50], ["я", "чувствую", 40], ["я", "люблю", 40],
      ["я", "не", 40], ["я", "могу", 35],
      ["мне", "нужно", 50], ["мне", "больно", 45], ["мне", "холодно", 40],
      ["мне", "жарко", 40], ["мне", "плохо", 40],
      ["да", "пожалуйста", 40], ["нет", "спасибо", 40],
      ["где", "он", 40], ["где", "она", 40], ["где", "это", 35],
      ["как", "дела", 45], ["как", "ты", 35],
      ["можно", "воды", 40], ["можно", "помочь", 35],
    ];

    for (const [word1, word2, freq] of seedBigrams) {
      const key = word1.toLowerCase();
      const val = word2.toLowerCase();

      // Seed into bigrams (for bigram lookup in predictNextWord step 3)
      if (!this.bigrams[key]) this.bigrams[key] = {};
      if (!this.bigrams[key][val] || this.bigrams[key][val] < freq) {
        this.bigrams[key][val] = freq;
      }

      // Also seed into phrasePairs (for phrase-pair lookup in step 1)
      if (!this.phrasePairs[key]) this.phrasePairs[key] = {};
      if (!this.phrasePairs[key][val] || this.phrasePairs[key][val] < freq) {
        this.phrasePairs[key][val] = freq;
      }
    }
  }

  /**
   * Extract word pairs from the seed dictionary phrases and add them as
   * bigrams. E.g. "אני רוצה לשתות" → "אני"→"רוצה", "רוצה"→"לשתות".
   * This ensures the communication board's vocabulary is immediately
   * available as predictions without the user needing to type them first.
   */
  private seedFromDictionary(): void {
    const SEED_FREQ = 40;
    for (const entry of seedDictionary) {
      const words = entry.text.toLowerCase().split(/\s+/).filter(Boolean);
      for (let i = 0; i < words.length - 1; i++) {
        const w1 = words[i];
        const w2 = words[i + 1];

        if (!this.bigrams[w1]) this.bigrams[w1] = {};
        if (!this.bigrams[w1][w2] || this.bigrams[w1][w2] < SEED_FREQ) {
          this.bigrams[w1][w2] = SEED_FREQ;
        }

        if (!this.phrasePairs[w1]) this.phrasePairs[w1] = {};
        if (!this.phrasePairs[w1][w2] || this.phrasePairs[w1][w2] < SEED_FREQ) {
          this.phrasePairs[w1][w2] = SEED_FREQ;
        }
      }
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(
        STORAGE_KEY_WORDS,
        JSON.stringify(Array.from(this.learnedWords.values()))
      );
      localStorage.setItem(STORAGE_KEY_BIGRAMS, JSON.stringify(this.bigrams));
      localStorage.setItem(
        STORAGE_KEY_PHRASES,
        JSON.stringify(Array.from(this.phrases.values()))
      );
      localStorage.setItem(STORAGE_KEY_RECENT, JSON.stringify(this.recentWords));
      localStorage.setItem(
        STORAGE_KEY_PHRASE_PAIRS,
        JSON.stringify(this.phrasePairs)
      );
    } catch (e) {
      console.error("[PredictionEngine] Failed to save to storage:", e);
    }

    // Trigger cloud sync callback
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }

  // ==================== Cloud Sync ====================

  setOnChangeCallback(cb: (() => void) | null): void {
    this.onChangeCallback = cb;
  }

  getLearnedData(): UserWordLearning {
    return {
      learned_words: Array.from(this.learnedWords.values()).map((w) => ({
        word: w.word,
        frequency: w.frequency,
        lastUsed: w.lastUsed,
        language: w.language as "he" | "en" | "ar" | "ru",
      })),
      learned_bigrams: this.bigrams as LearnedBigram,
      recent_words: this.recentWords,
    };
  }

  setCloudData(data: UserWordLearning): void {
    // Merge cloud learned words with local
    if (data.learned_words) {
      for (const w of data.learned_words) {
        const existing = this.learnedWords.get(w.word);
        if (!existing || w.frequency > existing.frequency) {
          this.learnedWords.set(w.word, {
            word: w.word,
            frequency: w.frequency,
            lastUsed: w.lastUsed,
            language: w.language,
          });
        }
      }
    }

    // Merge bigrams
    if (data.learned_bigrams) {
      for (const [prev, nextWords] of Object.entries(data.learned_bigrams)) {
        if (!this.bigrams[prev]) this.bigrams[prev] = {};
        for (const [next, freq] of Object.entries(nextWords)) {
          this.bigrams[prev][next] = Math.max(
            this.bigrams[prev][next] || 0,
            freq
          );
        }
      }
    }

    // Merge recent words
    if (data.recent_words) {
      const recentMap = new Map<string, number>();
      for (const r of this.recentWords) recentMap.set(r.word, r.timestamp);
      for (const r of data.recent_words) {
        const existing = recentMap.get(r.word);
        if (!existing || r.timestamp > existing) {
          recentMap.set(r.word, r.timestamp);
        }
      }
      this.recentWords = Array.from(recentMap.entries())
        .map(([word, timestamp]) => ({ word, timestamp }))
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 50);
    }

    this.saveToStorage();
  }

  // ==================== Main Search ====================

  /**
   * Main search with N-Gram Backoff (Trigram -> Bigram -> Unigram)
   */
  searchStandard(input: string): WordEntry[] {
    if (!input || !input.trim()) return [];

    const trimmed = input.trim();
    const isEndingInSpace = input.endsWith(" ");
    const lang = detectLanguage(trimmed);

    if (!isEndingInSpace && trimmed.length > 0) {
      // User is typing a word – do prefix search
      return this.prefixSearch(trimmed, lang);
    }

    // User just finished a word – predict next word with backoff
    return this.predictNextWord(trimmed, lang);
  }

  /**
   * Get suggestions (alias for searchStandard for the new API)
   */
  getSuggestions(input: string): WordEntry[] {
    return this.searchStandard(input);
  }

  /**
   * Prefix search: find words starting with the last partial word
   */
  private prefixSearch(input: string, lang: SupportedLanguage): WordEntry[] {
    const words = input.split(/\s+/);
    const lastWord = words[words.length - 1];
    if (!lastWord) return [];

    const results: WordEntry[] = [];
    const seen = new Set<string>();

    // 1. Check learned words first (personal vocabulary)
    for (const entry of this.learnedWords.values()) {
      if (
        entry.language === lang &&
        entry.word.toLowerCase().startsWith(lastWord.toLowerCase()) &&
        entry.word.toLowerCase() !== lastWord.toLowerCase()
      ) {
        if (!seen.has(entry.word)) {
          seen.add(entry.word);
          const recencyBoost =
            Date.now() - entry.lastUsed < RECENCY_BOOST_WINDOW ? 50 : 0;
          results.push({
            ...entry,
            frequency: entry.frequency + recencyBoost,
            isNextWord: false,
          });
        }
      }
    }

    // 2. Fill from vocabulary files
    if (results.length < MAX_SUGGESTIONS) {
      const vocabResults = searchVocabulary(
        lastWord,
        lang,
        MAX_SUGGESTIONS - results.length + 5
      );
      for (const word of vocabResults) {
        if (!seen.has(word)) {
          seen.add(word);
          results.push({
            word,
            frequency: 1,
            lastUsed: 0,
            language: lang,
            isNextWord: false,
          });
        }
        if (results.length >= MAX_SUGGESTIONS) break;
      }
    }

    return results
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, MAX_SUGGESTIONS);
  }

  /**
   * Predict next word using Trigram -> Bigram -> Unigram backoff
   */
  private predictNextWord(
    input: string,
    lang: SupportedLanguage
  ): WordEntry[] {
    const words = input.split(/\s+/).filter(Boolean);
    const predictions = new Map<string, WordEntry>();

    // Words already in the input — never suggest these back
    const inputWords = new Set(words.map(w => w.toLowerCase()));

    // 1. Check phrase pairs (e.g. "מרגיש" -> "שמח")
    if (words.length >= 1) {
      const lastWord = words[words.length - 1].toLowerCase();
      const pairNext = this.phrasePairs[lastWord];
      if (pairNext) {
        const sortedPairs = Object.entries(pairNext)
          .filter(([, freq]) => freq >= PHRASE_PAIR_THRESHOLD)
          .sort(([, a], [, b]) => b - a);
        for (const [word, freq] of sortedPairs) {
          if (!predictions.has(word) && !inputWords.has(word)) {
            predictions.set(word, {
              word,
              frequency: freq * 3,
              lastUsed: Date.now(),
              language: lang,
              isNextWord: true,
              isPhrasePair: true,
            });
          }
          if (predictions.size >= MAX_SUGGESTIONS) break;
        }
      }
    }

    // 2. Try Trigram (last 2 words)
    if (words.length >= 2) {
      const context =
        `${words[words.length - 2]} ${words[words.length - 1]}`.toLowerCase();
      this.addBigramPredictions(context, predictions, lang, 2.0);
    }

    // 3. Try Bigram (last 1 word)
    if (words.length >= 1) {
      const context = words[words.length - 1].toLowerCase();
      this.addBigramPredictions(context, predictions, lang, 1.5);
    }

    // 4. Unigram fallback (global frequency from learned + vocabulary)
    if (predictions.size < MAX_SUGGESTIONS) {
      // Learned words
      const learnedByFreq = Array.from(this.learnedWords.values())
        .filter((w) => w.language === lang)
        .sort((a, b) => b.frequency - a.frequency);
      for (const entry of learnedByFreq) {
        if (!predictions.has(entry.word)) {
          predictions.set(entry.word, {
            ...entry,
            isNextWord: true,
          });
        }
        if (predictions.size >= MAX_SUGGESTIONS) break;
      }
    }

    // 5. Hebrew grammar heuristics: suggest contextually appropriate words
    //    based on the grammatical role of the last word.
    if (predictions.size < MAX_SUGGESTIONS && lang === "he" && words.length >= 1) {
      const lastWord = words[words.length - 1].toLowerCase();
      let contextWords: string[] = [];

      // Question words → suggest answer-type words (nouns, pronouns, verbs)
      if (["מה", "מי", "איך", "איפה", "למה", "מתי", "כמה"].includes(lastWord)) {
        contextWords = ["זה", "הוא", "היא", "אפשר", "יש", "אני", "אתה", "שלי", "קורה", "נמצא"];
      }
      // Verbs that take infinitives (ל-)
      else if (["רוצה", "צריך", "צריכה", "חייב", "חייבת", "יכול", "יכולה", "אפשר", "מוכרח"].includes(lastWord)) {
        contextWords = ["ללכת", "לצאת", "לאכול", "לשתות", "לישון", "לראות", "לשמוע", "לדבר", "לנוח", "לקבל"];
      }
      // Pronouns → suggest common verbs
      else if (["אני", "אתה", "את", "הוא", "היא", "אנחנו", "הם", "הן"].includes(lastWord)) {
        contextWords = ["רוצה", "צריך", "מרגיש", "יכול", "חושב", "אוהב", "לא", "גם", "רק", "עוד"];
      }
      // "לא" → suggest what it negates
      else if (lastWord === "לא") {
        contextWords = ["רוצה", "יכול", "צריך", "טוב", "נעים", "נוח", "מרגיש", "יודע", "מבין", "רואה"];
      }
      // "יש" / "אין" → suggest possessive/existential continuations
      else if (["יש", "אין"].includes(lastWord)) {
        contextWords = ["לי", "לך", "לו", "לה", "להם", "פה", "שם", "הרבה", "מעט", "משהו", "בעיה"];
      }

      for (const word of contextWords) {
        if (!predictions.has(word) && !inputWords.has(word)) {
          predictions.set(word, {
            word,
            frequency: 3, // Above AAC vocab (2) but below bigrams
            lastUsed: 0,
            language: lang,
            isNextWord: true,
          });
        }
        if (predictions.size >= MAX_SUGGESTIONS) break;
      }
    }

    // 6. AAC-priority vocabulary (medical, care, emotions, daily needs)
    // Checked BEFORE the general 50k vocabulary so AAC-relevant words
    // like "pain", "thirsty", "nurse" appear before "however", "therefore".
    if (predictions.size < MAX_SUGGESTIONS) {
      const aacWords = AAC_PRIORITY_WORDS[lang] || [];
      for (const word of aacWords) {
        if (!predictions.has(word) && !inputWords.has(word)) {
          predictions.set(word, {
            word,
            frequency: 2, // Above general vocab (1) but below learned/bigrams
            lastUsed: 0,
            language: lang,
            isNextWord: true,
          });
        }
        if (predictions.size >= MAX_SUGGESTIONS) break;
      }
    }

    // 6. General vocabulary fallback (50k frequency list)
    if (predictions.size < MAX_SUGGESTIONS) {
      const vocabWords = getVocabulary(lang).slice(
        0,
        MAX_SUGGESTIONS * 3
      );
      for (const word of vocabWords) {
        if (!predictions.has(word)) {
          predictions.set(word, {
            word,
            frequency: 1,
            lastUsed: 0,
            language: lang,
            isNextWord: true,
          });
        }
        if (predictions.size >= MAX_SUGGESTIONS) break;
      }
    }

    // Final filter: remove any word already in the input (prevents "איך" → "איך")
    for (const w of inputWords) {
      predictions.delete(w);
    }

    return Array.from(predictions.values())
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, MAX_SUGGESTIONS);
  }

  private addBigramPredictions(
    contextKey: string,
    map: Map<string, WordEntry>,
    lang: SupportedLanguage,
    boost: number
  ): void {
    const nextWords = this.bigrams[contextKey];
    if (!nextWords) return;

    for (const [word, freq] of Object.entries(nextWords)) {
      if (!map.has(word)) {
        map.set(word, {
          word,
          frequency: freq * boost,
          lastUsed: Date.now(),
          language: lang,
          isNextWord: true,
          source: "local",
        });
      }
    }
  }

  // ==================== Learning ====================

  /**
   * Called when user selects a word suggestion
   */
  onWordSelected(word: string, fullPreviousText: string): void {
    const lang = detectLanguage(word);

    // Update learned words (unigram)
    const existing = this.learnedWords.get(word);
    if (existing) {
      existing.frequency += 5;
      existing.lastUsed = Date.now();
    } else {
      this.learnedWords.set(word, {
        word,
        frequency: 5,
        lastUsed: Date.now(),
        language: lang,
      });
    }

    // Update recent words
    this.recentWords = this.recentWords.filter((r) => r.word !== word);
    this.recentWords.unshift({ word, timestamp: Date.now() });
    if (this.recentWords.length > 50) this.recentWords = this.recentWords.slice(0, 50);

    const prevWords = fullPreviousText.trim().split(/\s+/).filter(Boolean);

    // Learn Bigram (prev 1 word -> current)
    if (prevWords.length >= 1) {
      const prev = prevWords[prevWords.length - 1].toLowerCase();
      this.updateBigram(prev, word.toLowerCase());

      // Track phrase pair
      if (!this.phrasePairs[prev]) this.phrasePairs[prev] = {};
      this.phrasePairs[prev][word.toLowerCase()] =
        (this.phrasePairs[prev][word.toLowerCase()] || 0) + 1;
    }

    // Learn Trigram (prev 2 words -> current)
    if (prevWords.length >= 2) {
      const prevTwo =
        `${prevWords[prevWords.length - 2]} ${prevWords[prevWords.length - 1]}`.toLowerCase();
      this.updateBigram(prevTwo, word.toLowerCase());
    }

    this.saveToStorage();
  }

  /**
   * Called when user selects a phrase (acronym mode)
   */
  onPhraseSelected(phrase: string, acronym: string): void {
    const key = acronym + ":" + phrase;
    const existing = this.phrases.get(key);
    if (existing) {
      existing.frequency += 5;
      existing.lastUsed = Date.now();
    } else {
      this.phrases.set(key, {
        phrase,
        acronym,
        frequency: 5,
        lastUsed: Date.now(),
        language: detectLanguage(phrase),
      });
    }
    this.saveToStorage();
  }

  private updateBigram(context: string, nextWord: string): void {
    if (!this.bigrams[context]) this.bigrams[context] = {};
    this.bigrams[context][nextWord] =
      (this.bigrams[context][nextWord] || 0) + 1;
  }

  // ==================== Acronym Search ====================

  searchAcronym(query: string): PhraseEntry[] {
    if (!query.trim()) return [];
    const lower = query.toLowerCase().replace(/\s/g, "");
    const lang = detectLanguage(query);

    const results: PhraseEntry[] = [];
    for (const entry of this.phrases.values()) {
      if (entry.language === lang && entry.acronym.startsWith(lower)) {
        results.push(entry);
      }
    }

    // Also search seed dictionary
    for (const entry of seedDictionary) {
      if (
        entry.language === lang &&
        entry.acronym.startsWith(lower)
      ) {
        results.push({
          phrase: entry.text,
          acronym: entry.acronym,
          frequency: entry.frequency,
          lastUsed: 0,
          language: entry.language as SupportedLanguage,
        });
      }
    }

    return results
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, MAX_SUGGESTIONS);
  }

  // ==================== AI Predictions (Pro Feature) ====================

  /**
   * Fetches context-aware predictions from Lovable AI Gateway
   * Should be called debounced (e.g., after user pauses typing for 600ms)
   */
  async fetchSmartPredictions(fullText: string): Promise<WordEntry[]> {
    if (!fullText || fullText.trim().length === 0) return [];

    try {
      const { data, error } = await supabase.functions.invoke('predict-next-word', {
        body: { text: fullText },
      });

      if (error) return [];

      const suggestions: string[] = data?.suggestions || [];
      const lang = detectLanguage(fullText);

      return suggestions.map((word: string) => ({
        word,
        frequency: 999,
        lastUsed: Date.now(),
        language: lang,
        isNextWord: true,
        source: "ai" as const,
      }));
    } catch {
      return [];
    }
  }
}

// ==================== Singleton ====================

let engineInstance: AACPredictionEngine | null = null;

export function getPredictionEngine(): AACPredictionEngine {
  if (!engineInstance) {
    engineInstance = new AACPredictionEngine();
  }
  return engineInstance;
}
